import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createAppPassword, deleteAppPassword, listAppPasswords, type NewAppPassword,
} from '../../api/account.ts';
import { useAuth } from '../../store/auth.ts';
import { formatDateTime, formatRelative } from '../../lib/format.ts';
import { toast } from '../../store/toast.ts';
import { ConfirmDialog } from '../common/ConfirmDialog.tsx';
import { IconCopy, IconDevice } from '../../lib/icons.tsx';

/** 탐색기 주소창에 넣을 WebDAV 주소. 지금 보고 있는 호스트를 그대로 쓴다. */
function davUrl(): string {
  return `${window.location.origin}/dav`;
}

async function copy(text: string, what: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${what}를 복사했습니다`);
  } catch {
    toast.error('복사할 수 없습니다. 직접 선택해 복사해 주세요.');
  }
}

/**
 * 네트워크 드라이브(WebDAV) 탭.
 *
 * 프로그램을 설치하지 않고 윈도우 탐색기에서 바로 쓰는 방법을 안내하고,
 * 거기에 넣을 앱 비밀번호를 관리한다.
 */
export function DriveTab() {
  const queryClient = useQueryClient();
  const user = useAuth((s) => s.user);
  const [label, setLabel] = useState('');
  const [issued, setIssued] = useState<NewAppPassword | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ id: string; label: string } | null>(null);

  const list = useQuery({ queryKey: ['app-passwords'], queryFn: listAppPasswords });

  const create = useMutation({
    mutationFn: createAppPassword,
    onSuccess: (created) => {
      // 원문은 지금 한 번만 볼 수 있다. 화면에 띄워 두고 사용자가 닫을 때까지 남긴다.
      setIssued(created);
      setLabel('');
      void queryClient.invalidateQueries({ queryKey: ['app-passwords'] });
    },
    onError: (e: Error) => { toast.error(e); },
  });

  const remove = useMutation({
    mutationFn: deleteAppPassword,
    onSuccess: () => {
      toast.success('앱 비밀번호를 폐기했습니다');
      setConfirmDelete(null);
      void queryClient.invalidateQueries({ queryKey: ['app-passwords'] });
    },
    onError: (e: Error) => { toast.error(e); setConfirmDelete(null); },
  });

  const items = list.data?.items ?? [];
  const account = user?.email ?? '';

  return (
    <>
      <section className="panel">
        <h2>네트워크 드라이브로 쓰기</h2>
        <p className="panel__hint">
          프로그램을 설치하지 않고 윈도우 탐색기에서 바로 열 수 있습니다.
          파일을 PC 에 내려두지 않으므로 용량을 차지하지 않는 대신, 인터넷이
          끊기면 파일도 열리지 않습니다.
        </p>

        <ol className="steps">
          <li>
            아래에서 <b>앱 비밀번호를 하나 만듭니다.</b>
          </li>
          <li>
            탐색기에서 <b>내 PC → 네트워크 드라이브 연결</b>을 누릅니다.
          </li>
          <li>
            폴더 칸에 이 주소를 넣습니다.
            <div className="codeline">
              <code>{davUrl()}</code>
              <button
                className="btn btn--sm"
                onClick={() => { void copy(davUrl(), '주소'); }}
                aria-label="주소 복사"
              >
                <IconCopy size={14} /> 복사
              </button>
            </div>
          </li>
          <li>
            아이디는 <code>{account}</code>, 비밀번호는 만든 <b>앱 비밀번호</b>를 넣습니다.
            계정 비밀번호가 아닙니다.
          </li>
        </ol>
      </section>

      <section className="panel">
        <h2>앱 비밀번호</h2>
        <p className="panel__hint">
          윈도우는 이 비밀번호를 자격증명 관리자에 계속 보관합니다. 그래서 계정
          비밀번호가 아니라 파일 접근만 되는 별도 비밀번호를 씁니다. PC 마다
          따로 만들어 두면 한 대를 잃어버렸을 때 그것만 끊을 수 있습니다.
        </p>

        <form
          className="row"
          style={{ gap: 8, marginBottom: 12 }}
          onSubmit={(e) => { e.preventDefault(); create.mutate(label.trim()); }}
        >
          <input
            className="input spacer"
            placeholder="어디에 쓸 것인지 (예: 집 데스크톱)"
            maxLength={60}
            value={label}
            onChange={(e) => { setLabel(e.target.value); }}
          />
          <button
            className="btn btn--primary"
            type="submit"
            disabled={label.trim() === '' || create.isPending}
          >
            {create.isPending ? <span className="spinner" /> : '만들기'}
          </button>
        </form>

        {issued && (
          <div className="issued">
            <div className="issued__head">
              <strong>{issued.label} 용 비밀번호</strong>
              <span className="faint">지금만 볼 수 있습니다</span>
            </div>
            <div className="codeline">
              <code className="issued__pw">{issued.password}</code>
              <button
                className="btn btn--sm"
                onClick={() => { void copy(issued.password, '비밀번호'); }}
              >
                <IconCopy size={14} /> 복사
              </button>
            </div>
            <p className="issued__note">
              창을 닫으면 다시 볼 수 없습니다. 서버는 해시만 저장하므로 잊으면
              새로 만들어야 합니다.
            </p>
            <button className="btn btn--sm" onClick={() => { setIssued(null); }}>
              확인했습니다
            </button>
          </div>
        )}

        {list.isLoading && <span className="spinner" />}
        {!list.isLoading && items.length === 0 && (
          <p className="muted">아직 만든 앱 비밀번호가 없습니다.</p>
        )}

        {items.map((item) => (
          <div key={item.id} className="panel__row">
            <IconDevice size={18} />
            <div className="spacer" style={{ minWidth: 0 }}>
              <strong style={{ fontSize: 13 }}>{item.label}</strong>
              <div className="faint" style={{ fontSize: 12 }}>
                {formatDateTime(item.created_at)}
                {' 만듦 · '}
                {item.last_used_at
                  ? `마지막 사용 ${formatRelative(item.last_used_at)}${
                      item.last_used_ip ? ` (${item.last_used_ip})` : ''
                    }`
                  : '아직 쓰지 않음'}
              </div>
            </div>
            <button
              className="btn btn--sm"
              onClick={() => { setConfirmDelete({ id: item.id, label: item.label }); }}
            >
              폐기
            </button>
          </div>
        ))}
      </section>

      <section className="panel">
        <h2>잘 안 될 때</h2>
        <ul className="tips">
          <li>
            <b>연결이 안 됩니다</b> — 윈도우의 <code>WebClient</code> 서비스가 꺼져
            있을 수 있습니다. <code>services.msc</code> 에서 [WebClient] 를 찾아
            시작하고 시작 유형을 [자동] 으로 바꾸세요.
          </li>
          <li>
            <b>50MB 보다 큰 파일이 안 열립니다</b> — 윈도우 기본 제한입니다.
            관리자 권한 명령 프롬프트에서 아래를 실행하고 재부팅하세요.
            <div className="codeline">
              <code>
                reg add
                {' "HKLM\\SYSTEM\\CurrentControlSet\\Services\\WebClient\\Parameters"'}
                {' /v FileSizeLimitInBytes /t REG_DWORD /d 4294967295 /f'}
              </code>
            </div>
          </li>
          <li>
            <b>파일을 여러 PC 에서 같이 쓰고 오프라인에서도 봐야 합니다</b> —
            네트워크 드라이브는 파일을 PC 에 두지 않습니다. 그런 용도라면
            동기화 프로그램을 쓰세요.
          </li>
        </ul>
      </section>

      {confirmDelete && (
        <ConfirmDialog
          title="앱 비밀번호를 폐기할까요?"
          message={`'${confirmDelete.label}' 로 연결해 둔 드라이브는 바로 끊어집니다.`}
          confirmLabel="폐기"
          danger
          onConfirm={() => { remove.mutate(confirmDelete.id); }}
          onCancel={() => { setConfirmDelete(null); }}
        />
      )}
    </>
  );
}
